import { asArray, asBoolean, asInteger, asJsonRecord, asNumber, asPositiveInteger, asRecord, asString, asUint32, assertUniqueIds } from "../document/validate.ts";
import { MAX_MAP_DIM } from "../effects/limits.ts";
import type { AssetManifest } from "../assets/manifest.ts";
import type { CompiledWorld } from "./compile.ts";
import { plainSeamList, readSeam } from "../document/layout-file.ts";
import type { JsonValue, MapPlace, Seam, TileRef } from "../document/types.ts";
import type { PropFootprint } from "./footprint.ts";
import { pageSetDrawn } from "../assets/budget.ts";
import { framesUsedByMap } from "./frames-used.ts";
import { namedFields, readNamedFiles, type NamedFiles } from "./named-files.ts";

/**
 * The world manifest (Playbook 5.8): everything a client or server needs to locate any chunk of a published
 * world version from the manifest alone. Paths are relative to the version directory, which is also the R2
 * key prefix `worlds/<worldId>/<version>/`.
 */

/**
 * Client and server paths of one chunk.
 */
export interface ManifestChunk {
  readonly client: string;
  readonly server: string;
}

/**
 * One map's entry. `properties` holds the map's fields as the author set them (ADR-052's amendment of
 * 2026-09-26), present only when the map holds any. The layout fields are optional and additive (ADR-109 decision
 * 11): `place` is the map's place on the world grid; `group` its map group's id and name; `blank` is true for a map
 * whose compile wrote no chunk, which then lists none and uses no page; `pages` is the indices, ascending, of the
 * asset manifest's atlas pages the map's client chunks draw from, written by a build that has the asset manifest.
 * `indoor` is true for a map whose light follows no sky, and `dim` (above 0, at most `MAX_MAP_DIM`) how dark it is held at
 * every hour (ADR-141 decision D4); both are absent on a sky map, and a client reads absent as the sky.
 */
export interface ManifestMap {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly playersAbove?: number;
  readonly properties?: { readonly [key: string]: JsonValue };
  readonly place?: MapPlace;
  readonly group?: { readonly id: string; readonly name: string };
  readonly blank?: true;
  readonly indoor?: true;
  readonly dim?: number;
  readonly chunks: { readonly [key: string]: ManifestChunk };
  readonly pages?: readonly number[];
}

/**
 * The manifest. `contract` is the path of the version's game contract (ADR-052) and `files` the paths of the
 * other files the game wrote into the version beside the chunks, in path order (P1.4-T14), so a reader can tell
 * a file the version never had from one that went missing: a named file that cannot be read is an error, while
 * a manifest written before files were named carries neither field and its readers keep their older rules. `seams` are
 * the world's seams as stored (ADR-147): the state and crossings a map server reads to know which edges may be crossed,
 * present only when the world has any. `lightMarks` is true for a version whose compile wrote the indoor mark of every map
 * that lies indoors (ADR-141 decision D4): a map without `indoor` in such a version follows the sky, whereas in a version
 * compiled before the marks existed an absent mark says nothing.
 */
export interface WorldManifest {
  readonly worldId: string;
  readonly version: string;
  readonly schemaVersion: number;
  readonly tileSize: number;
  readonly chunkSize: number;
  readonly assetManifest: string;
  readonly contract?: string;
  readonly files?: readonly string[];
  readonly lightMarks?: true;
  readonly maps: readonly ManifestMap[];
  readonly objectAppearances?: readonly ManifestObjectAppearance[];
  readonly seams?: readonly Seam[];
}

/**
 * How one lowered behaviour prop looks and blocks (ADR-053): the frame, the footprint resolved to runs
 * (null when the state stamps nothing) and the activation cells of each state, the index of the state the
 * thing starts in, and the prop's mirroring. Every thing carries the index of its entry in the snapshot's
 * `appearance` field, so a client draws and a map server stamps without the compile context. `transitions` holds
 * the frames a client plays once when it sees the state change between two indices (ADR-058 decision 5);
 * it is absent when no transition of the appearance has frames, and a change with no entry is a cut.
 * `inside` is present only on the appearance of a prop driven by presence (ADR-058 amendment, P1.3-T4):
 * the index of the state a viewer's own client shows while that viewer's character stands in the prop's
 * area, whose cells are the initial state's `activation`, the ground under the art when it holds none. Such
 * a prop is no thing: it stays in its chunk's prop layer carrying the index, and the server holds nothing.
 * `layer`, `sortPixels` and `offset` say where a thing draws, as the editor draws the prop it was lowered from
 * (P1.1-T7): the index of its prop layer among the map's compiled layers (collision layers folded, the index
 * `layerDepth` takes), the prop's sort offset, and the pixels from the anchor `objectAnchor` gives over the
 * feet point the server holds to the author's anchor. A thing's appearance always carries all three; one
 * written without them draws at the band's lowest layer with no sort offset, on the feet point's anchor.
 * `order` is a thing's tie key in its layer (P1.6-T42), which every compile writes on a thing, so no two things
 * of a layer share an appearance; one written without it draws with none.
 * `soundSet` is the sound set the prop names (ADR-058's amendment of 2026-10-13): the value of its type's sound set
 * field when that is an option other than the default, a string the game reads and Daedalus does not. It is
 * written last and only when present, so an appearance without one is written as it always was, and a reader
 * built before it ignores it.
 */
export interface ManifestObjectAppearance {
  readonly states: readonly ManifestAppearanceState[];
  readonly transitions?: readonly ManifestAppearanceTransition[];
  readonly initial: number;
  readonly inside?: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly layer?: number;
  readonly sortPixels?: number;
  readonly offset?: ManifestPixelOffset;
  readonly order?: number;
  readonly soundSet?: string;
}

/**
 * A distance in world pixels, possibly fractional where a frame of odd width centres on a half pixel.
 */
export interface ManifestPixelOffset {
  readonly x: number;
  readonly y: number;
}

/**
 * The animation a client plays once when a thing's state index changes from `from` to `to`, two different
 * indices into the appearance's states: the frames in order and how long each stays, in milliseconds.
 * Purely cosmetic (ADR-058 amendment): the new state's footprint applies the instant the index changes.
 */
export interface ManifestAppearanceTransition {
  readonly from: number;
  readonly to: number;
  readonly frames: readonly TileRef[];
  readonly durations: readonly number[];
}

/**
 * One state of an appearance: the frame it shows, that frame's pixel width as the atlas cuts it (P1.1-T6;
 * 0 only from a context that could size the frame neither by its rect nor by a footprint), what it stamps,
 * and the cells it is usable from. `activation` holds `[column, rowsUp]` cells in the frame's own space,
 * already mirrored with the prop.
 */
export interface ManifestAppearanceState {
  readonly id: string;
  readonly frame: TileRef;
  readonly width: number;
  readonly footprint: PropFootprint | null;
  readonly activation: readonly (readonly [number, number])[];
}

/**
 * A chunk's written paths, as the writer reports them.
 */
export interface ChunkPaths {
  readonly mapId: string;
  readonly cx: number;
  readonly cy: number;
  readonly client: string;
  readonly server: string;
}

const INDENT = 2;
const KEY = /^(0|[1-9][0-9]*)_(0|[1-9][0-9]*)$/;

/**
 * The manifest key of a chunk position.
 *
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @returns `<cx>_<cy>`.
 */
export function chunkKey(cx: number, cy: number): string {
  return `${cx}_${cy}`;
}

/**
 * Number of chunks along one axis.
 *
 * @param size Map size in tiles.
 * @param chunkSize Chunk size in tiles.
 * @returns Chunk count.
 */
function chunksAlong(size: number, chunkSize: number): number {
  return Math.ceil(size / chunkSize);
}

/**
 * The atlas pages one compiled map draws from (ADR-109 decision 11), closed over the looping animations its frames start,
 * whose frames the tile animator swaps in and so may sit on pages the map's own frames do not.
 *
 * @param map The compiled map.
 * @param compiled The compiled world, for its appearance table.
 * @param assets The asset manifest the world's frames refer to.
 * @returns The page indices, ascending and without repeats; empty for a blank map.
 * @throws Error naming the map and the frame when a frame the map draws is not in the asset manifest.
 */
function pagesOf(map: CompiledWorld["maps"][number], compiled: CompiledWorld, assets: AssetManifest): number[] {
  const ids = framesUsedByMap(map, compiled.objectAppearances);
  for (const id of ids) {
    if (assets.frames[id] === undefined) {
      throw new Error(`map ${map.id} draws frame ${id}, which the asset manifest lacks`);
    }
  }
  return pageSetDrawn(ids, assets);
}

/**
 * Builds the manifest for a compiled world from the paths the writer produced.
 *
 * @param compiled The compiled world.
 * @param version Version label.
 * @param written Every written chunk's paths.
 * @param assetManifest Path of the asset manifest the world's frames refer to.
 * @param named The contract and the game's files the version directory carries beside the chunks; omitted for a
 *   version that names none.
 * @param assets The asset manifest at `assetManifest`; when given, every map's entry carries `pages`.
 * @returns The manifest, maps and chunks in compile order, the game's files in path order.
 * @throws Error when a compiled chunk has no written paths, a written path belongs to no compiled chunk, a named
 *   path is not a plain relative path inside the version, is named twice or names the contract as a file, or a map
 *   draws a frame the asset manifest lacks.
 */
export function buildManifest(compiled: CompiledWorld, version: string, written: readonly ChunkPaths[], assetManifest: string, named?: NamedFiles, assets?: AssetManifest): WorldManifest {
  const files = named === undefined ? undefined : readNamedFiles(named.contract, named.files);
  const paths = new Map(written.map((w) => [`${w.mapId} ${chunkKey(w.cx, w.cy)}`, w]));
  const claimed = new Set<string>();
  const maps = compiled.maps.map((map) => {
    const chunks: Record<string, ManifestChunk> = {};
    for (const chunk of map.chunks) {
      const id = `${map.id} ${chunkKey(chunk.cx, chunk.cy)}`;
      const entry = paths.get(id);
      if (entry === undefined) {
        throw new Error(`chunk ${id} was compiled but not written`);
      }
      claimed.add(id);
      chunks[chunkKey(chunk.cx, chunk.cy)] = { client: entry.client, server: entry.server };
    }
    return { id: map.id, width: map.width, height: map.height, playersAbove: map.playersAbove, ...(map.properties === undefined ? {} : { properties: map.properties }), ...(map.place === undefined ? {} : { place: map.place }), ...(map.group === undefined ? {} : { group: map.group }), ...(map.blank === true ? { blank: true as const } : {}), ...(map.indoor === true ? { indoor: true as const } : {}), ...(map.dim === undefined ? {} : { dim: map.dim }), chunks, ...(assets === undefined ? {} : { pages: pagesOf(map, compiled, assets) }) };
  });
  for (const id of paths.keys()) {
    if (!claimed.has(id)) {
      throw new Error(`chunk ${id} was written but not compiled`);
    }
  }
  return { worldId: compiled.worldId, version, schemaVersion: compiled.schemaVersion, tileSize: compiled.tileSize, chunkSize: compiled.chunkSize, assetManifest, ...namedFields(files), lightMarks: true as const, maps, ...(compiled.objectAppearances.length === 0 ? {} : { objectAppearances: compiled.objectAppearances }), ...(compiled.seams === undefined || compiled.seams.length === 0 ? {} : { seams: compiled.seams }) };
}

/**
 * Resolves a chunk's path from the manifest.
 *
 * @param manifest The manifest.
 * @param mapId Map id.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param side Which chunk file.
 * @returns The path relative to the version directory.
 * @throws Error when the map is unknown or the position lies outside its chunk grid.
 */
export function resolveChunk(manifest: WorldManifest, mapId: string, cx: number, cy: number, side: "client" | "server"): string {
  const map = manifest.maps.find((m) => m.id === mapId);
  if (map === undefined) {
    throw new Error(`manifest for ${manifest.worldId} has no map ${mapId}`);
  }
  const entry = map.chunks[chunkKey(cx, cy)];
  if (entry === undefined) {
    throw new Error(`map ${mapId} has no chunk ${chunkKey(cx, cy)}`);
  }
  return entry[side];
}

/**
 * Serialises a manifest with fixed key order.
 *
 * @param manifest The manifest.
 * @returns Pretty-printed JSON.
 */
export function serialiseManifest(manifest: WorldManifest): string {
  const plain = {
    worldId: manifest.worldId,
    version: manifest.version,
    schemaVersion: manifest.schemaVersion,
    tileSize: manifest.tileSize,
    chunkSize: manifest.chunkSize,
    assetManifest: manifest.assetManifest,
    ...(manifest.contract === undefined ? {} : { contract: manifest.contract }),
    ...(manifest.files === undefined ? {} : { files: [...manifest.files] }),
    ...(manifest.lightMarks === true ? { lightMarks: true } : {}),
    maps: manifest.maps.map((map) => ({
      id: map.id,
      width: map.width,
      height: map.height,
      ...(map.playersAbove === undefined ? {} : { playersAbove: map.playersAbove }),
      ...(map.properties === undefined ? {} : { properties: map.properties }),
      ...(map.place === undefined ? {} : { place: { x: map.place.x, y: map.place.y, ...(map.place.depth === undefined ? {} : { depth: map.place.depth }) } }),
      ...(map.group === undefined ? {} : { group: { id: map.group.id, name: map.group.name } }),
      ...(map.blank === true ? { blank: true } : {}),
      ...(map.indoor === true ? { indoor: true } : {}),
      ...(map.dim === undefined ? {} : { dim: map.dim }),
      chunks: Object.fromEntries(Object.keys(map.chunks).map((key) => [key, { client: map.chunks[key]?.client, server: map.chunks[key]?.server }])),
      ...(map.pages === undefined ? {} : { pages: [...map.pages] }),
    })),
    ...(manifest.objectAppearances === undefined ? {} : { objectAppearances: manifest.objectAppearances.map(plainAppearance) }),
    ...(manifest.seams === undefined ? {} : { seams: plainSeamList(manifest.seams) }),
  };
  return JSON.stringify(plain, null, INDENT);
}

/**
 * Serialises a world's object appearance table exactly as the manifest carries it, with the same fixed key
 * order and no indentation, so equal tables give equal text. A version label hashes this text (ADR-052),
 * since a change confined to the table, a transition's frames or a state's activation cells, moves no chunk.
 *
 * @param appearances The table.
 * @returns Compact JSON text.
 */
export function serialiseObjectAppearances(appearances: readonly ManifestObjectAppearance[]): string {
  return JSON.stringify(appearances.map(plainAppearance));
}

/**
 * One object appearance with a fixed key order: states, then transitions when it has any, then the initial
 * index, the inside index of a presence appearance, the mirroring, and a thing's layer, sort offset,
 * pixel offset and tie key when it carries them.
 *
 * @param appearance The appearance.
 * @returns The plain object.
 */
function plainAppearance(appearance: ManifestObjectAppearance): Record<string, unknown> {
  return {
    states: appearance.states.map((state) => ({
      id: state.id,
      frame: { tileset: state.frame.tileset, tile: state.frame.tile },
      width: state.width,
      footprint: state.footprint === null ? null : { w: state.footprint.w, h: state.footprint.h, base: state.footprint.base, runs: state.footprint.runs.map(([rows, start, end]) => [rows, start, end]) },
      activation: state.activation.map(([column, rowsUp]) => [column, rowsUp]),
    })),
    ...(appearance.transitions === undefined ? {} : { transitions: appearance.transitions.map((t) => ({ from: t.from, to: t.to, frames: t.frames.map((f) => ({ tileset: f.tileset, tile: f.tile })), durations: [...t.durations] })) }),
    initial: appearance.initial,
    ...(appearance.inside === undefined ? {} : { inside: appearance.inside }),
    flipX: appearance.flipX,
    flipY: appearance.flipY,
    ...(appearance.layer === undefined ? {} : { layer: appearance.layer }),
    ...(appearance.sortPixels === undefined ? {} : { sortPixels: appearance.sortPixels }),
    ...(appearance.offset === undefined ? {} : { offset: { x: appearance.offset.x, y: appearance.offset.y } }),
    ...(appearance.order === undefined ? {} : { order: appearance.order }),
    ...(appearance.soundSet === undefined ? {} : { soundSet: appearance.soundSet }),
  };
}

/**
 * Reads one map entry, checking that its chunk keys are exactly its grid.
 *
 * @param value Candidate value.
 * @param chunkSize Chunk size in tiles.
 * @param path Path for error messages.
 * @returns The map entry.
 * @throws Error when a chunk of the grid is missing from a map that is not blank or a key lies outside it, a blank map
 *   lists a chunk or a page, or the blank mark, the place, the group or the pages are malformed.
 */
function readMap(value: unknown, chunkSize: number, path: string): ManifestMap {
  const record = asRecord(value, path);
  const id = asString(record["id"], `${path}.id`);
  const width = asPositiveInteger(record["width"], `${path}.width`);
  const height = asPositiveInteger(record["height"], `${path}.height`);
  const chunksRecord = asRecord(record["chunks"], `${path}.chunks`);
  const columns = chunksAlong(width, chunkSize);
  const rows = chunksAlong(height, chunkSize);
  const chunks: Record<string, ManifestChunk> = {};
  for (const key of Object.keys(chunksRecord)) {
    const match = KEY.exec(key);
    if (match === null || Number(match[1]) >= columns || Number(match[2]) >= rows) {
      throw new Error(`${path}.chunks.${key} is outside the ${columns}x${rows} chunk grid`);
    }
    const entry = asRecord(chunksRecord[key], `${path}.chunks.${key}`);
    chunks[key] = { client: asString(entry["client"], `${path}.chunks.${key}.client`), server: asString(entry["server"], `${path}.chunks.${key}.server`) };
  }
  const blank = record["blank"] === undefined ? false : readBlank(record["blank"], `${path}.blank`);
  if (blank && Object.keys(chunks).length > 0) {
    throw new Error(`${path} is blank and lists chunks`);
  }
  for (let cy = 0; cy < rows && !blank; cy += 1) {
    for (let cx = 0; cx < columns; cx += 1) {
      if (chunks[chunkKey(cx, cy)] === undefined) {
        throw new Error(`${path}.chunks is missing ${chunkKey(cx, cy)}`);
      }
    }
  }
  const place = record["place"] === undefined ? undefined : readPlace(record["place"], `${path}.place`);
  const group = record["group"] === undefined ? undefined : readGroup(record["group"], `${path}.group`);
  const indoor = record["indoor"] === undefined ? false : readIndoor(record["indoor"], `${path}.indoor`);
  const dim = record["dim"] === undefined ? undefined : readDim(record["dim"], `${path}.dim`);
  const pages = record["pages"] === undefined ? undefined : readPages(record["pages"], blank, `${path}.pages`);
  const playersAbove = record["playersAbove"] === undefined ? undefined : asUint32(record["playersAbove"], `${path}.playersAbove`);
  const properties = record["properties"] === undefined ? undefined : asJsonRecord(record["properties"], `${path}.properties`);
  return { id, width, height, ...(playersAbove === undefined ? {} : { playersAbove }), ...(properties === undefined ? {} : { properties }), ...(place === undefined ? {} : { place }), ...(group === undefined ? {} : { group }), ...(blank ? { blank: true as const } : {}), ...(indoor ? { indoor: true as const } : {}), ...(dim === undefined ? {} : { dim }), chunks, ...(pages === undefined ? {} : { pages }) };
}

/**
 * Reads a map's indoor mark.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns True.
 * @throws Error when the value is not the boolean true, the only value a writer gives.
 */
function readIndoor(value: unknown, path: string): true {
  if (value !== true) {
    throw new Error(`${path} must be true when present`);
  }
  return true;
}

/**
 * Reads an indoor map's dim.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The dim.
 * @throws Error when it is not a number above 0 and at most `MAX_MAP_DIM`, the only values a writer gives.
 */
function readDim(value: unknown, path: string): number {
  const dim = asNumber(value, path);
  if (!(dim > 0 && dim <= MAX_MAP_DIM)) {
    throw new Error(`${path} must be above 0 and at most ${MAX_MAP_DIM}`);
  }
  return dim;
}

/**
 * Reads a map's blank mark.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns True.
 * @throws Error when the value is not the boolean true, the only value a writer gives.
 */
function readBlank(value: unknown, path: string): true {
  if (value !== true) {
    throw new Error(`${path} must be true when present`);
  }
  return true;
}

/**
 * Reads a map's place: whole tile positions and an optional whole depth.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The place.
 * @throws Error when a coordinate or the depth is not an integer.
 */
function readPlace(value: unknown, path: string): MapPlace {
  const record = asRecord(value, path);
  const depth = record["depth"] === undefined ? undefined : asInteger(record["depth"], `${path}.depth`);
  return { x: asInteger(record["x"], `${path}.x`), y: asInteger(record["y"], `${path}.y`), ...(depth === undefined ? {} : { depth }) };
}

/**
 * Reads a map's group: its id and name.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The group.
 * @throws Error when the id or the name is not a string.
 */
function readGroup(value: unknown, path: string): { readonly id: string; readonly name: string } {
  const record = asRecord(value, path);
  return { id: asString(record["id"], `${path}.id`), name: asString(record["name"], `${path}.name`) };
}

/**
 * Reads a map's atlas pages: ascending, without repeats.
 *
 * @param value The value.
 * @param blank Whether the map is blank, which uses no page.
 * @param path Path for messages.
 * @returns The page indices.
 * @throws Error when an index is not a non-negative integer, the list is not strictly ascending, or a blank map lists one.
 */
function readPages(value: unknown, blank: boolean, path: string): number[] {
  const pages = asArray(value, path).map((page, i) => asUint32(page, `${path}[${i}]`));
  if (pages.some((page, i) => i > 0 && page <= (pages[i - 1] as number))) {
    throw new Error(`${path} must be ascending without repeats`);
  }
  if (blank && pages.length > 0) {
    throw new Error(`${path} lists pages for a blank map`);
  }
  return pages;
}

/**
 * Reads the manifest's light-marks flag.
 *
 * @param value Candidate value.
 * @returns True.
 * @throws Error when the value is not `true`.
 */
function readLightMarks(value: unknown): true {
  if (value !== true) {
    throw new Error("lightMarks must be true when present");
  }
  return true;
}

/**
 * Parses and validates manifest text.
 *
 * @param text JSON text.
 * @returns The manifest.
 * @throws SyntaxError when the text is not JSON; Error naming the offending path when the structure is
 *   invalid, a map's chunk keys do not cover exactly its grid, or a file it names beside its chunks is not a
 *   plain relative path inside the version, is named twice or is the contract.
 */
export function deserialiseManifest(text: string): WorldManifest {
  const record = asRecord(JSON.parse(text) as unknown, "manifest");
  const worldId = asString(record["worldId"], "worldId");
  const version = asString(record["version"], "version");
  const schemaVersion = asNumber(record["schemaVersion"], "schemaVersion");
  const tileSize = asPositiveInteger(record["tileSize"], "tileSize");
  const chunkSize = asPositiveInteger(record["chunkSize"], "chunkSize");
  const assetManifest = asString(record["assetManifest"], "assetManifest");
  const named = record["contract"] === undefined ? undefined : readNamedFiles(record["contract"], record["files"] === undefined ? [] : asArray(record["files"], "files"));
  const maps = asArray(record["maps"], "maps").map((item, i) => readMap(item, chunkSize, `maps[${i}]`));
  assertUniqueIds(maps.map((m) => m.id), "maps");
  const objectAppearances = record["objectAppearances"] === undefined ? undefined : asArray(record["objectAppearances"], "objectAppearances").map((item, i) => readAppearance(item, `objectAppearances[${i}]`));
  const lightMarks = record["lightMarks"] === undefined ? false : readLightMarks(record["lightMarks"]);
  const seams = record["seams"] === undefined ? undefined : asArray(record["seams"], "seams").map((item, i) => readSeam(item, `seams[${i}]`));
  return { worldId, version, schemaVersion, tileSize, chunkSize, assetManifest, ...namedFields(named), ...(lightMarks ? { lightMarks: true as const } : {}), maps, ...(objectAppearances === undefined ? {} : { objectAppearances }), ...(seams === undefined || seams.length === 0 ? {} : { seams }) };
}

/**
 * Reads one object appearance of a manifest (ADR-053).
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The appearance.
 * @throws Error when a state is malformed, a state id repeats, the initial index names no state, the
 *   inside index of a presence appearance names no state or the initial one, or a thing's layer is not a
 *   whole index, its sort offset not an integer, its pixel offset not a pair of numbers or its tie key not a
 *   whole number.
 */
function readAppearance(value: unknown, path: string): ManifestObjectAppearance {
  const record = asRecord(value, path);
  const states = asArray(record["states"], `${path}.states`).map((item, i) => {
    const state = asRecord(item, `${path}.states[${i}]`);
    const frame = asRecord(state["frame"], `${path}.states[${i}].frame`);
    const footprint = state["footprint"] === null ? null : readFootprint(state["footprint"], `${path}.states[${i}].footprint`);
    const activation = asArray(state["activation"], `${path}.states[${i}].activation`).map((cell, c) => readCell(cell, `${path}.states[${i}].activation[${c}]`));
    return { id: asString(state["id"], `${path}.states[${i}].id`), frame: { tileset: asString(frame["tileset"], `${path}.states[${i}].frame.tileset`), tile: asInteger(frame["tile"], `${path}.states[${i}].frame.tile`) }, width: asInteger(state["width"], `${path}.states[${i}].width`), footprint, activation };
  });
  assertUniqueIds(states.map((s) => s.id), `${path}.states`);
  const initial = asInteger(record["initial"], `${path}.initial`);
  if (initial < 0 || initial >= states.length) {
    throw new Error(`${path}.initial ${initial} is not one of its ${states.length} states`);
  }
  const transitions = record["transitions"] === undefined ? undefined : readTransitions(record["transitions"], states.length, `${path}.transitions`);
  const inside = record["inside"] === undefined ? undefined : readStateIndex(record["inside"], states.length, `${path}.inside`);
  if (inside === initial) {
    throw new Error(`${path}.inside ${inside} is its initial state`);
  }
  const layer = record["layer"] === undefined ? undefined : asUint32(record["layer"], `${path}.layer`);
  const sortPixels = record["sortPixels"] === undefined ? undefined : asInteger(record["sortPixels"], `${path}.sortPixels`);
  const offset = record["offset"] === undefined ? undefined : readOffset(record["offset"], `${path}.offset`);
  const order = record["order"] === undefined ? undefined : asUint32(record["order"], `${path}.order`);
  const soundSet = record["soundSet"] === undefined ? undefined : asString(record["soundSet"], `${path}.soundSet`);
  return {
    states,
    ...(transitions === undefined ? {} : { transitions }),
    initial,
    ...(inside === undefined ? {} : { inside }),
    flipX: asBoolean(record["flipX"], `${path}.flipX`),
    flipY: asBoolean(record["flipY"], `${path}.flipY`),
    ...(layer === undefined ? {} : { layer }),
    ...(sortPixels === undefined ? {} : { sortPixels }),
    ...(offset === undefined ? {} : { offset }),
    ...(order === undefined ? {} : { order }),
    ...(soundSet === undefined ? {} : { soundSet }),
  };
}

/**
 * Reads a thing's pixel offset.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The offset.
 * @throws Error when the value is not an object of two finite numbers.
 */
function readOffset(value: unknown, path: string): ManifestPixelOffset {
  const record = asRecord(value, path);
  return { x: asNumber(record["x"], `${path}.x`), y: asNumber(record["y"], `${path}.y`) };
}

/**
 * Reads one state index of a transition.
 *
 * @param value The value.
 * @param stateCount How many states the appearance has.
 * @param path Path for messages.
 * @returns The index.
 * @throws Error when the value is not an integer naming one of the states.
 */
function readStateIndex(value: unknown, stateCount: number, path: string): number {
  const index = asInteger(value, path);
  if (index < 0 || index >= stateCount) {
    throw new Error(`${path} ${index} is not one of its ${stateCount} states`);
  }
  return index;
}

/**
 * Reads an appearance's transitions (ADR-058): each between two different state indices, at most one per
 * ordered pair, with at least one frame and one positive duration per frame.
 *
 * @param value The value.
 * @param stateCount How many states the appearance has.
 * @param path Path for messages.
 * @returns The transitions.
 * @throws Error when a transition names a state the appearance lacks or the same state twice, repeats a
 *   pair, or its frames and durations are malformed or of different lengths.
 */
function readTransitions(value: unknown, stateCount: number, path: string): ManifestAppearanceTransition[] {
  const seen = new Set<string>();
  return asArray(value, path).map((item, i) => {
    const at = `${path}[${i}]`;
    const record = asRecord(item, at);
    const from = readStateIndex(record["from"], stateCount, `${at}.from`);
    const to = readStateIndex(record["to"], stateCount, `${at}.to`);
    if (from === to) {
      throw new Error(`${at} goes from state ${from} to itself`);
    }
    if (seen.has(`${from} ${to}`)) {
      throw new Error(`${at} repeats the change from state ${from} to ${to}`);
    }
    seen.add(`${from} ${to}`);
    const frames = asArray(record["frames"], `${at}.frames`).map((frame, f) => {
      const ref = asRecord(frame, `${at}.frames[${f}]`);
      return { tileset: asString(ref["tileset"], `${at}.frames[${f}].tileset`), tile: asInteger(ref["tile"], `${at}.frames[${f}].tile`) };
    });
    const durations = asArray(record["durations"], `${at}.durations`).map((duration, d) => asPositiveInteger(duration, `${at}.durations[${d}]`));
    if (frames.length === 0) {
      throw new Error(`${at} has no frame`);
    }
    if (durations.length !== frames.length) {
      throw new Error(`${at}.durations must hold one duration per frame`);
    }
    return { from, to, frames, durations };
  });
}

/**
 * Reads one `[column, rowsUp]` activation cell.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The cell.
 * @throws Error when the value is not a pair of integers.
 */
function readCell(value: unknown, path: string): readonly [number, number] {
  const pair = asArray(value, path);
  if (pair.length !== 2) {
    throw new Error(`${path} must be a [column, rowsUp] pair`);
  }
  return [asInteger(pair[0], `${path}[0]`), asInteger(pair[1], `${path}[1]`)];
}

/**
 * Reads a state's resolved footprint: frame size, base row and `[rowsAboveBase, start, end]` runs.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The footprint.
 * @throws Error when a field is missing, not an integer, or a run is not a triple.
 */
function readFootprint(value: unknown, path: string): PropFootprint {
  const record = asRecord(value, path);
  const runs = asArray(record["runs"], `${path}.runs`).map((run, r) => {
    const triple = asArray(run, `${path}.runs[${r}]`);
    if (triple.length !== 3) {
      throw new Error(`${path}.runs[${r}] must be a [rowsAboveBase, start, end] triple`);
    }
    return [asInteger(triple[0], `${path}.runs[${r}][0]`), asInteger(triple[1], `${path}.runs[${r}][1]`), asInteger(triple[2], `${path}.runs[${r}][2]`)] as const;
  });
  return { w: asPositiveInteger(record["w"], `${path}.w`), h: asPositiveInteger(record["h"], `${path}.h`), base: asPositiveInteger(record["base"], `${path}.base`), runs };
}
